import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { BUILT_IN_CHART_TYPES } from '../../../constants';
import { getChartFormByType } from '@/reducers/dashboardChartsReducer';

import {
  useCreateRevisionCodeMutation,
  useUpdateRevisionCodeMutation
} from '@experiment-management-shared';
import { Panel } from '../../../types';
import { BasePythonPanelFooter } from './BasePythonPanelFooter';
import { noop } from 'lodash';
import { Button } from '@ds';
import SaveTemplatePopover from './BasePythonPanelFooter/Components/SaveTemplatePopover';

interface PythonPanelFooterProps {
  handleReset: () => void;
  hideGoBackButton?: boolean;
  onAddBuiltInPanel: () => void;
  onEditBuiltInPanel: (
    chartId: string,
    chartFormsMap: Record<string, Panel>,
    chartType: string
  ) => void;
  onEditCustomPanel: (chartId: string, libraryTemplateInstance: object) => void;
  hasCodeBeenUpdated: boolean;
  projectId: string;
  chartFormsMap: Record<string, Panel>;
  handleCloseModal: () => void;
  resetInitialChartState: () => void;
}

const PythonPanelFooter = ({
  handleReset,
  hideGoBackButton,
  onAddBuiltInPanel,
  onEditBuiltInPanel,
  onEditCustomPanel,
  projectId,
  chartFormsMap,
  hasCodeBeenUpdated,
  handleCloseModal,
  resetInitialChartState
}: PythonPanelFooterProps) => {
  const history = useHistory();
  const chartType = BUILT_IN_CHART_TYPES.python;
  const chartForm = useSelector(state =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    getChartFormByType(state, { chartType })
  );

  const [isTemplatePopoverOpen, setTemplatePopoverOpen] = React.useState(false);

  const queryParams = new URLSearchParams(history.location.hash);
  const chartId = queryParams.get('chartId');

  const updateRevisionMutation = useUpdateRevisionCodeMutation();
  const createRevisionMutation = useCreateRevisionCodeMutation(projectId);

  const handleAddChart = () => {
    const newChart = {
      chartType,
      ...chartForm
    };

    createRevisionMutation.mutate({
      chart: newChart,
      onAddPanel: onAddBuiltInPanel
    });
  };

  const handleEditChart = (chartId: string) => {
    // only if code is modified we need to update the revision
    // otherwise we just need to update the chart form
    if (hasCodeBeenUpdated) {
      updateRevisionMutation.mutate({
        chart: chartFormsMap[chartType],
        onEditPanel: onEditBuiltInPanel,
        chartId,
        chartFormsMap,
        chartType,
        onCloseModal: noop
      });
    }
    onEditBuiltInPanel(chartId, chartFormsMap, chartType);
  };

  const handleDone = () => {
    if (chartId) {
      handleEditChart(chartId);
    } else {
      handleAddChart();
    }
    handleCloseModal();
  };

  const renderSaveButton = () => (
    <>
      {isTemplatePopoverOpen && (
        <SaveTemplatePopover
          chartType={chartType}
          isOpen={isTemplatePopoverOpen}
          onEditCustomChart={onEditCustomPanel}
          onClose={() => setTemplatePopoverOpen(false)}
          resetInitialChartState={resetInitialChartState}
        />
      )}
      <Button
        data-test="save-template-button"
        size="large"
        type="tertiary"
        onClick={() => setTemplatePopoverOpen(true)}
      >
        Save to library
      </Button>
    </>
  );

  return (
    <BasePythonPanelFooter
      actions={{
        onReset: handleReset,
        onDone: handleDone,
        onAdd: handleAddChart
      }}
      renderCustomActions={renderSaveButton}
      settings={{ hideGoBackButton, hasCodeBeenUpdated }}
    />
  );
};

export default PythonPanelFooter;
